import type { ListAuditQuery } from '@clockwise/clients/audit';
import { ApiError } from '@clockwise/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { History } from 'lucide-react';
import { useState } from 'react';
import { AuditEntryList } from '@/components/custom/audit/audit-entry-list';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { auditApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

const PAGE_SIZE = 25;

/**
 * Unlock audit trail behind a "Unlock history" button → opens a paginated side
 * sheet (matching the unlock add/edit sheet) showing who created / edited /
 * revoked each unlock and when. Filtered to `entityType=timesheet_unlocks`;
 * the server gates this read on `timesheet.settings` (the same permission as
 * the unlock screen), so it never widens what the viewer can see. The query
 * only fires while the sheet is open, and create/edit/revoke invalidate
 * `audit.all` so an open sheet refreshes. `showObjectType` surfaces each row's
 * scope label ("Jane Doe · Acme Inc") next to the summary.
 */
export function TimesheetUnlockAudit() {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);

  const query = {
    entityType: 'timesheet_unlocks',
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  } satisfies ListAuditQuery;

  const audit = useQuery({
    queryKey: queryKeys.audit.list(query),
    queryFn: () => auditApi.list(query),
    enabled: open,
    placeholderData: keepPreviousData,
  });

  const total = audit.data?.meta.total ?? 0;
  const pageCount = audit.data?.data.length ?? 0;
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min(total, page * PAGE_SIZE + pageCount);

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setPage(0);
      }}
    >
      <SheetTrigger asChild>
        {/* `SheetTrigger asChild` clobbers this Button's own `data-slot="button"`
            with its own `data-slot="sheet-trigger"` (Radix Slot lets the outer
            wrapper's explicit props win), so the global `[data-slot='button']`
            pill-radius rule never matches this trigger. Set the radius directly
            instead of depending on that rule. */}
        <Button variant="outline" size="sm" className="rounded-full">
          <History />
          Unlock history
        </Button>
      </SheetTrigger>
      <SheetContent className="flex flex-col gap-0 sm:max-w-2xl">
        <SheetHeader className="border-b">
          <SheetTitle>Unlock history</SheetTitle>
          <SheetDescription>
            Every unlock created, edited, or revoked — who did it and when.
          </SheetDescription>
        </SheetHeader>

        {audit.isLoading ? (
          <div className="p-4">
            <Skeleton className="h-48 w-full" />
          </div>
        ) : audit.isError ? (
          <p className="p-4 text-sm text-destructive">
            {audit.error instanceof ApiError
              ? audit.error.message
              : 'Failed to load unlock history.'}
          </p>
        ) : (
          <>
            {/* Pass total=pageCount so AuditEntryList's own "first N of total"
                note never renders — this sheet owns pagination below. */}
            <div className="min-h-0 flex-1 overflow-y-auto">
              <AuditEntryList items={audit.data?.data ?? []} total={pageCount} showObjectType />
            </div>
            <footer className="flex items-center justify-between border-t p-4 text-xs text-muted-foreground">
              <span>
                {total === 0 ? 'No results' : `Showing ${showingFrom}–${showingTo} of ${total}`}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0 || audit.isFetching}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={(page + 1) * PAGE_SIZE >= total || audit.isFetching}
                >
                  Next
                </Button>
              </div>
            </footer>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
